import { supabase } from './supabase';

export interface EmailTemplate {
  name: string;
  subject_template: string;
  html_template: string;
  text_template?: string;
  description?: string;
}

export interface NotificationData {
  [key: string]: string | number | boolean;
}

export interface InAppNotification {
  id: string;
  user_id: string;
  event_type: string;
  message: string;
  link?: string;
  read: boolean;
  created_at: string;
}

export class NotificationService {
  // Remplacer les placeholders dans un template
  private static replacePlaceholders(template: string, data: NotificationData): string {
    let result = template;
    
    Object.entries(data).forEach(([key, value]) => {
      const placeholder = `{${key}}`;
      result = result.replace(new RegExp(placeholder, 'g'), String(value));
    });
    
    return result;
  }

  // Récupérer un template d'email
  private static async getEmailTemplate(templateName: string): Promise<EmailTemplate | null> {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('name', templateName)
        .single();

      if (error) {
        console.error('Error fetching email template:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getEmailTemplate:', error);
      return null;
    }
  }

  // Vérifier les préférences de notification d'un utilisateur
  private static async shouldSendEmailNotification(
    userId: string, 
    templateName: string
  ): Promise<boolean> {
    try {
      // D'abord vérifier si l'utilisateur a activé les notifications email globalement
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('email_notifications_enabled')
        .eq('id', userId)
        .single();

      if (userError) {
        console.error('Error fetching user email preferences:', userError);
        return true; // Par défaut, activer les notifications
      }

      // Si les notifications email sont désactivées globalement, ne pas envoyer
      if (!userData.email_notifications_enabled) {
        console.log(`Email notifications globally disabled for user ${userId}`);
        return false;
      }

      // Ensuite vérifier les préférences spécifiques au template
      const { data, error } = await supabase
        .from('user_notification_preferences')
        .select('enabled')
        .eq('user_id', userId)
        .eq('template_name', templateName)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user notification preference:', error);
        return true; // Par défaut, activer les notifications
      }

      // Si aucune préférence n'est définie, activer par défaut
      return data?.enabled ?? true;
    } catch (error) {
      console.error('Error in getUserNotificationPreference:', error);
      return true;
    }
  }

  // Envoyer un email basé sur un template
  public static async sendTemplatedEmail(
    templateName: string,
    recipientEmail: string,
    data: NotificationData
  ): Promise<void> {
    try {
      // Récupérer l'utilisateur destinataire
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('email', recipientEmail)
        .maybeSingle();

      if (userError) {
        console.error('Error fetching user for email:', userError);
        return;
      }

      // Si l'utilisateur existe, vérifier ses préférences
      if (userData) {
        const isEnabled = await this.shouldSendEmailNotification(userData.id, templateName);
        if (!isEnabled) {
          console.log(`Email notification disabled for user ${recipientEmail} (template: ${templateName})`);
          return;
        }
      }

      // Récupérer le template
      const template = await this.getEmailTemplate(templateName);
      if (!template) {
        console.error(`Email template not found: ${templateName}`);
        return;
      }

      // Remplacer les placeholders
      const subject = this.replacePlaceholders(template.subject_template, data);
      const html = this.replacePlaceholders(template.html_template, data);
      const text = template.text_template 
        ? this.replacePlaceholders(template.text_template, data)
        : html.replace(/<[^>]*>/g, ''); // Strip HTML tags

      // Envoyer l'email
      const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-email', {
        body: {
          to: recipientEmail,
          subject,
          html,
          text
        }
      });

      if (emailError) {
        console.error('Error sending templated email:', emailError);
        throw emailError;
      }

      console.log('Templated email sent successfully:', emailResult);
    } catch (error) {
      console.error('Error in sendTemplatedEmail:', error);
      throw error;
    }
  }

  // Créer une notification interne
  public static async createInAppNotification(
    userId: string,
    eventType: string,
    message: string,
    link?: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('in_app_notifications')
        .insert({
          user_id: userId,
          event_type: eventType,
          message,
          link
        });

      if (error) {
        console.error('Error creating in-app notification:', error);
        throw error;
      }

      console.log('In-app notification created successfully');
    } catch (error) {
      console.error('Error in createInAppNotification:', error);
      throw error;
    }
  }

  // Créer des notifications pour plusieurs utilisateurs
  public static async createInAppNotifications(
    notifications: Array<{
      userId: string;
      eventType: string;
      message: string;
      link?: string;
    }>
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('in_app_notifications')
        .insert(
          notifications.map(notif => ({
            user_id: notif.userId,
            event_type: notif.eventType,
            message: notif.message,
            link: notif.link
          }))
        );

      if (error) {
        console.error('Error creating in-app notifications:', error);
        throw error;
      }

      console.log('In-app notifications created successfully');
    } catch (error) {
      console.error('Error in createInAppNotifications:', error);
      throw error;
    }
  }

  // Marquer une notification comme lue
  public static async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('in_app_notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in markNotificationAsRead:', error);
      throw error;
    }
  }

  // Marquer toutes les notifications d'un utilisateur comme lues
  public static async markAllNotificationsAsRead(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('in_app_notifications')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in markAllNotificationsAsRead:', error);
      throw error;
    }
  }

  // Récupérer les notifications d'un utilisateur
  public static async getUserNotifications(
    userId: string,
    limit: number = 50,
    unreadOnly: boolean = false
  ): Promise<InAppNotification[]> {
    try {
      let query = supabase
        .from('in_app_notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (unreadOnly) {
        query = query.eq('read', false);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching user notifications:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserNotifications:', error);
      return [];
    }
  }

  // Compter les notifications non lues d'un utilisateur
  public static async getUnreadNotificationCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('in_app_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) {
        console.error('Error counting unread notifications:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getUnreadNotificationCount:', error);
      return 0;
    }
  }

  // Supprimer les anciennes notifications (plus de 30 jours)
  public static async cleanupOldNotifications(): Promise<void> {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { error } = await supabase
        .from('in_app_notifications')
        .delete()
        .lt('created_at', thirtyDaysAgo.toISOString());

      if (error) {
        console.error('Error cleaning up old notifications:', error);
        throw error;
      }

      console.log('Old notifications cleaned up successfully');
    } catch (error) {
      console.error('Error in cleanupOldNotifications:', error);
      throw error;
    }
  }
}

// Fonctions utilitaires pour les notifications spécifiques
export class TransactionNotifications {
  // Notification pour nouvelle transaction
  public static async notifyNewTransaction(
    transactionId: string,
    transactionData: {
      property_number: string;
      seller: string;
      buyer: string;
      sale_price: number;
      agencies: string[];
      negotiators: string[];
      created_by: string;
    }
  ): Promise<void> {
    try {
      console.log('Sending new transaction notifications for:', transactionData);

      // Récupérer les informations du créateur
      const { data: creator, error: creatorError } = await supabase
        .from('users')
        .select('firstname, lastname')
        .eq('id', transactionData.created_by)
        .single();

      if (creatorError) throw creatorError;

      // Récupérer tous les utilisateurs des agences concernées + tous les admins
      const { data: agencyUsers, error: agencyUsersError } = await supabase
        .from('users')
        .select('id, email, firstname, lastname')
        .or(transactionData.agencies.map(agency => `agency.ilike.%${agency}%`).join(','));

      if (agencyUsersError) throw agencyUsersError;

      // Récupérer tous les administrateurs (ils reçoivent toujours les notifications)
      const { data: adminUsers, error: adminUsersError } = await supabase
        .from('users')
        .select('id, email, firstname, lastname')
        .eq('role', 'admin');

      if (adminUsersError) throw adminUsersError;

      // Combiner les utilisateurs et supprimer les doublons
      const allUsers = [...(agencyUsers || []), ...(adminUsers || [])];
      const uniqueUsers = allUsers.filter((user, index, self) => 
        index === self.findIndex(u => u.id === user.id)
      );

      console.log('Users to notify:', uniqueUsers.map(u => ({ email: u.email, name: `${u.firstname} ${u.lastname}` })));

      const emailData = {
        property_number: transactionData.property_number,
        seller: transactionData.seller,
        buyer: transactionData.buyer,
        sale_price_formatted: new Intl.NumberFormat('fr-FR', { 
          style: 'currency', 
          currency: 'EUR' 
        }).format(transactionData.sale_price),
        agency: transactionData.agencies.join(', '),
        negotiator: transactionData.negotiators.join(', ')
      };

      // Envoyer les emails en respectant les préférences utilisateur
      const emailPromises = uniqueUsers.map(user => 
        NotificationService.sendTemplatedEmail(
          'new_transaction_notification',
          user.email,
          emailData
        )
      );

      // Créer les notifications internes
      const inAppNotifications = uniqueUsers.map(user => ({
        userId: user.id,
        eventType: 'transaction_created',
        message: `Nouvelle transaction #${transactionData.property_number} créée par ${creator.firstname} ${creator.lastname}`,
        link: `/view-transaction/${transactionId}`
      }));

      await Promise.all([
        ...emailPromises,
        NotificationService.createInAppNotifications(inAppNotifications)
      ]);

      console.log('New transaction notifications sent successfully to', uniqueUsers.length, 'users');

    } catch (error) {
      console.error('Error in notifyNewTransaction:', error);
      throw error; // Re-throw pour permettre le debugging
    }
  }

  // Notification pour changement de statut
  public static async notifyStatusChange(
    transactionId: string,
    oldStatus: string,
    newStatus: string
  ): Promise<void> {
    try {
      // Récupérer les détails de la transaction
      const { data: transaction, error } = await supabase
        .from('transactions')
        .select(`
          property_number,
          seller,
          buyer,
          transaction_agencies (
            agency
          ),
          transaction_negotiators (
            negotiator:negotiators (
              firstname,
              lastname
            )
          )
        `)
        .eq('id', transactionId)
        .single();

      if (error) throw error;

      const agencyNames = transaction.transaction_agencies.map(ta => ta.agency);
      const negotiatorName = transaction.transaction_negotiators
        .map(tn => `${tn.negotiator.firstname} ${tn.negotiator.lastname}`)
        .join(', ');

      // Récupérer les utilisateurs des agences concernées
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, email, firstname, lastname')
        .or(agencyNames.map(agency => `agency.ilike.%${agency}%`).join(','));

      if (usersError) throw usersError;

      const statusLabels: { [key: string]: string } = {
        'potentiel': 'Potentiel',
        'compromis': 'Compromis',
        'acte': 'Acté',
        'encaisse': 'Encaissé',
        'cloture': 'Clôturé',
        'annule': 'Annulé'
      };

      const emailData = {
        property_number: transaction.property_number,
        seller: transaction.seller,
        buyer: transaction.buyer,
        old_status: statusLabels[oldStatus] || oldStatus,
        new_status: statusLabels[newStatus] || newStatus,
        agency: agencyNames.join(', '),
        negotiator: negotiatorName
      };

      // Envoyer les emails
      const emailPromises = users.map(user => 
        NotificationService.sendTemplatedEmail(
          'status_change_notification',
          user.email,
          emailData
        )
      );

      // Créer les notifications internes
      const inAppNotifications = users.map(user => ({
        userId: user.id,
        eventType: 'transaction_status_change',
        message: `Transaction #${transaction.property_number} : ${statusLabels[oldStatus]} → ${statusLabels[newStatus]}`,
        link: `/validation?transaction=${transactionId}`
      }));

      await Promise.all([
        ...emailPromises,
        NotificationService.createInAppNotifications(inAppNotifications)
      ]);

    } catch (error) {
      console.error('Error in notifyStatusChange:', error);
    }
  }

  // Notification pour demande de régularisation
  public static async notifyRegularizationRequest(
    transactionId: string,
    requestedBy: string,
    comment: string
  ): Promise<void> {
    try {
      // Récupérer les détails de la transaction
      const { data: transaction, error } = await supabase
        .from('transactions')
        .select(`
          property_number,
          seller,
          buyer,
          transaction_agencies (
            agency
          )
        `)
        .eq('id', transactionId)
        .single();

      if (error) throw error;

      // Récupérer les informations du demandeur
      const { data: requester, error: requesterError } = await supabase
        .from('users')
        .select('firstname, lastname')
        .eq('id', requestedBy)
        .single();

      if (requesterError) throw requesterError;

      // Récupérer les administrateurs
      const { data: admins, error: adminsError } = await supabase
        .from('users')
        .select('id, email, firstname, lastname')
        .eq('role', 'admin');

      if (adminsError) throw adminsError;

      const emailData = {
        property_number: transaction.property_number,
        seller: transaction.seller,
        buyer: transaction.buyer,
        requested_by: `${requester.firstname} ${requester.lastname}`,
        comment,
        agency: transaction.transaction_agencies.map(ta => ta.agency).join(', ')
      };

      // Envoyer les emails aux administrateurs
      const emailPromises = admins.map(admin => 
        NotificationService.sendTemplatedEmail(
          'regularization_request_notification',
          admin.email,
          emailData
        )
      );

      // Créer les notifications internes pour les administrateurs
      const inAppNotifications = admins.map(admin => ({
        userId: admin.id,
        eventType: 'regul_request_created',
        message: `Demande de régularisation pour #${transaction.property_number} par ${requester.firstname} ${requester.lastname}`,
        link: `/regul?transaction=${transactionId}`
      }));

      await Promise.all([
        ...emailPromises,
        NotificationService.createInAppNotifications(inAppNotifications)
      ]);

    } catch (error) {
      console.error('Error in notifyRegularizationRequest:', error);
    }
  }

  // Notification pour approbation/rejet de régularisation
  public static async notifyRegularizationApproval(
    transactionId: string,
    approved: boolean,
    reviewedBy: string,
    adminComment?: string
  ): Promise<void> {
    try {
      // Récupérer les détails de la transaction et la demande
      const { data: regulRequest, error } = await supabase
        .from('regul_requests')
        .select(`
          requested_by,
          user:requested_by (
            email,
            firstname,
            lastname
          ),
          transaction:transactions (
            property_number,
            seller,
            buyer
          )
        `)
        .eq('transaction_id', transactionId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;

      // Récupérer les informations du reviewer
      const { data: reviewer, error: reviewerError } = await supabase
        .from('users')
        .select('firstname, lastname')
        .eq('id', reviewedBy)
        .single();

      if (reviewerError) throw reviewerError;

      const emailData = {
        property_number: regulRequest.transaction.property_number,
        seller: regulRequest.transaction.seller,
        buyer: regulRequest.transaction.buyer,
        approval_status: approved ? 'approuvée' : 'rejetée',
        approval_status_upper: approved ? 'APPROUVÉE' : 'REJETÉE',
        approval_icon: approved ? '✅' : '❌',
        header_color: approved ? '#10B981, #059669' : '#EF4444, #DC2626',
        result_bg: approved ? '#ecfdf5' : '#fef2f2',
        result_border: approved ? '#a7f3d0' : '#fecaca',
        status_color: approved ? '#059669' : '#dc2626',
        reviewed_by: `${reviewer.firstname} ${reviewer.lastname}`,
        admin_comment_section: adminComment ? `
          <div class="comment">
            <h4>Commentaire de l'administrateur :</h4>
            <p>${adminComment}</p>
          </div>
        ` : '',
        admin_comment_text: adminComment ? `Commentaire : ${adminComment}` : '',
        result_message: approved ? 
          'Les modifications ont été appliquées à la transaction.' : 
          'La transaction reste dans son état précédent.'
      };

      // Envoyer l'email au demandeur
      await NotificationService.sendTemplatedEmail(
        'regularization_approval_notification',
        regulRequest.user.email,
        emailData
      );

      // Créer une notification interne pour le demandeur
      await NotificationService.createInAppNotification(
        regulRequest.requested_by,
        approved ? 'regul_request_approved' : 'regul_request_rejected',
        `Votre demande de régularisation pour #${regulRequest.transaction.property_number} a été ${approved ? 'approuvée' : 'rejetée'}`,
        `/validation?transaction=${transactionId}`
      );

    } catch (error) {
      console.error('Error in notifyRegularizationApproval:', error);
    }
  }

  // Notification de rappel d'objectifs
  public static async notifyObjectiveReminder(
    recipientEmail: string,
    data: {
      negotiatorName: string;
      currentSales: number;
      objective: number;
      completionRate: number;
      remaining: number;
      period: string;
    }
  ): Promise<void> {
    try {
      const emailData = {
        negotiator_name: data.negotiatorName,
        period: data.period,
        objective_formatted: new Intl.NumberFormat('fr-FR', { 
          style: 'currency', 
          currency: 'EUR', 
          maximumFractionDigits: 0 
        }).format(data.objective),
        current_sales_formatted: new Intl.NumberFormat('fr-FR', { 
          style: 'currency', 
          currency: 'EUR', 
          maximumFractionDigits: 0 
        }).format(data.currentSales),
        remaining_formatted: new Intl.NumberFormat('fr-FR', { 
          style: 'currency', 
          currency: 'EUR', 
          maximumFractionDigits: 0 
        }).format(Math.abs(data.remaining)),
        remaining_color: data.remaining > 0 ? '#dc2626' : '#059669',
        completion_rate: data.completionRate.toFixed(1),
        completion_rate_capped: Math.min(data.completionRate, 100).toString(),
        completion_color: data.completionRate >= 100 ? '#059669' : 
                         data.completionRate >= 75 ? '#0ea5e9' : '#dc2626'
      };

      await NotificationService.sendTemplatedEmail(
        'objective_reminder_notification',
        recipientEmail,
        emailData
      );

    } catch (error) {
      console.error('Error in notifyObjectiveReminder:', error);
    }
  }
}